import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Banknote,
  CalendarCheck,
  Check,
  Clock8,
  Crosshair,
  FileWarning,
  Search,
  ShieldCheck,
  Smartphone,
  TrendingDown,
  X,
  Zap,
} from "lucide-react";
import { EASE, MaskedLine, Reveal, SectionHead } from "../components/Reveal";
import { DeviceMockup } from "../components/DeviceMockup";
import { Marquee } from "../components/Marquee";
import { BeforeAfter } from "../components/BeforeAfter";
import { DemoFirstNote } from "../components/DemoFirstNote";
import { SpotsBadge } from "../components/SpotsBadge";
import { OfferBar } from "../components/OfferBar";
import { FAQAccordion } from "../components/FAQAccordion";
import { CTABand } from "../components/CTABand";
import { SITE } from "../config";

const PAINS = [
  { icon: Banknote, title: "Agencies quote $3k–$10k", text: "Mostly overhead, for a five-page site." },
  { icon: Clock8, title: "DIY eats your weekends", text: "Three weekends in, it still looks like a template." },
  { icon: TrendingDown, title: "Pretty sites, zero leads", text: "If it doesn't capture names, it's a brochure." },
];

const BENEFITS = [
  { icon: Crosshair, title: "Built for lead capture", text: "Every page points at a form or a booking." },
  { icon: Zap, title: "Live in 72 hours", text: "First cut in 48h. Final site in 72h." },
  { icon: Smartphone, title: "Mobile-first, always", text: "Designed for the phone in their hand." },
  { icon: CalendarCheck, title: "Bookings on autopilot", text: "Your Calendly, embedded in the site." },
  { icon: Search, title: "On-page SEO done right", text: "Titles, headings, speed and structure." },
  { icon: ShieldCheck, title: "Zero technical friction", text: "Hosting, SSL and launch handled for you." },
];

const STEPS = [
  { n: "01", title: "Book a free demo", text: "20 minutes. No obligation." },
  { n: "02", title: "Share your business", text: "A short questionnaire. Rough notes are fine." },
  { n: "03", title: "First cut in 48h", text: "A real draft. One round of feedback." },
  { n: "04", title: "Live in 72h", text: "Polished, tested, capturing leads." },
];

const TRUST = [
  {
    icon: Check,
    title: "Transparent pricing",
    text: `$${SITE.price} today, $${SITE.renewalPerYear}/year after year one. Both numbers are here, not in a sales call.`,
  },
  {
    icon: Zap,
    title: "A clock you can check",
    text: "First cut in 48h, live in 72h — the same promise in every contract.",
  },
  {
    icon: ShieldCheck,
    title: "You own everything",
    text: "Your domain, your content, your files. Leave anytime.",
  },
  {
    icon: X,
    title: "What's NOT included",
    text: "No e-commerce, custom apps or paid ads. Extra pages quoted upfront.",
  },
];

const Home = () => (
  <>
    {/* HERO */}
    <section className="hero-glow grain relative overflow-hidden pb-14 pt-28 sm:pb-20 sm:pt-36" data-testid="hero-section">
      <div className="container-x relative grid items-center gap-12 lg:grid-cols-2 lg:gap-8">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <SpotsBadge />
          </motion.div>

          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-[3.4rem]">
            <MaskedLine delay={0.12}>A website that does</MaskedLine>
            <MaskedLine delay={0.22}>more than look good.</MaskedLine>
            <MaskedLine delay={0.32} className="text-volt glow-text">
              It gets you leads.
            </MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
          >
            <OfferBar className="mt-6 max-w-lg" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.58, ease: EASE }}
            className="mt-4 max-w-lg"
          >
            <DemoFirstNote />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.64, ease: EASE }}
            className="mt-4 max-w-lg text-sm leading-relaxed text-mist sm:text-base"
          >
            Professional, lead-generating websites for small businesses. No agency prices. No DIY
            weekends.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.68, ease: EASE }}
            className="mt-7 flex flex-wrap items-center gap-3 sm:gap-4"
          >
            <Link to="/book-demo" data-testid="hero-primary-cta" className="btn-primary">
              Book a Free Demo <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
            <Link to="/how-it-works" data-testid="hero-secondary-cta" className="btn-secondary">
              See How It Works
            </Link>
          </motion.div>

          <p
            className="mt-5 font-mono text-[10px] uppercase tracking-[0.18em] text-dim sm:text-xs"
            data-testid="hero-trust-line"
          >
            Founding offer · First {SITE.totalSpots} customers only
          </p>
        </div>

        <div className="hidden lg:block">
          <DeviceMockup />
        </div>
      </div>
    </section>

    <Marquee />

    {/* PROBLEM */}
    <section className="container-x py-14 sm:py-20" data-testid="problem-section">
      <SectionHead
        eyebrow="The problem"
        title="Getting a good website shouldn't be this hard"
      />
      <div className="mt-8 grid gap-3 sm:mt-10 sm:gap-4 md:grid-cols-3">
        {PAINS.map((pain, i) => (
          <Reveal key={pain.title} delay={i * 0.08}>
            <div className="card-surface flex h-full items-start gap-4 p-5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-neon/15 text-neon">
                <pain.icon size={18} />
              </span>
              <div>
                <h3 className="font-display text-base font-semibold text-white">{pain.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-mist">{pain.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* BEFORE / AFTER */}
    <section className="border-y border-white/5 bg-card/40 py-14 sm:py-20" data-testid="before-after-section">
      <div className="container-x">
        <SectionHead
          eyebrow="Before / After"
          title="Same business. One rebuilt to sell."
          lead="Drag the handle: left is the site most small businesses are stuck with, right is what we hand you in 72 hours."
        />
        <div className="mt-8 sm:mt-10">
          <Reveal className="mx-auto max-w-4xl">
            <BeforeAfter />
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <DemoFirstNote testId="before-after-demo-first-note" />
            <Link to="/book-demo" data-testid="before-after-cta" className="btn-primary">
              See Mine for Free <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    {/* BENEFITS */}
    <section className="container-x py-14 sm:py-20" data-testid="benefits-section">
      <SectionHead
        eyebrow="What you get"
        title="Not just a website. A lead machine."
        lead={`Six things every site does — all inside the flat $${SITE.price}.`}
      />
      <div className="mt-8 grid gap-3 sm:mt-10 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
        {BENEFITS.map((b, i) => (
          <Reveal key={b.title} delay={(i % 3) * 0.07}>
            <div className="card-surface h-full p-5 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-volt/30">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-volt/10 text-volt">
                <b.icon size={18} />
              </span>
              <h3 className="mt-4 font-display text-base font-semibold text-white">{b.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-mist">{b.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* HOW IT WORKS */}
    <section className="border-y border-white/5 bg-card/40 py-14 sm:py-20" data-testid="how-it-works-section">
      <div className="container-x">
        <SectionHead
          eyebrow="How it works"
          title="Demo call to live site in four steps"
        />
        <div className="mt-8 grid gap-3 sm:mt-10 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
          {STEPS.map((step, i) => (
            <Reveal key={step.n} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-white/[0.08] bg-ink p-5">
                <span className="font-mono text-sm font-bold text-volt">{step.n}</span>
                <h3 className="mt-3 font-display text-base font-semibold text-white">{step.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-mist">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <Link to="/how-it-works" data-testid="hiw-detail-link" className="btn-secondary">
              See the full 48h/72h timeline
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    {/* PRICING */}
    <section className="container-x py-14 sm:py-20" data-testid="pricing-section">
      <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-12">
        <div>
          <SectionHead
            eyebrow="Pricing"
            title="One price. Everything in. No surprises."
            lead={`$${SITE.price} flat, one time — design, build, lead capture and a full year of hosting. Then $${SITE.renewalPerYear}/year, cancel anytime.`}
          />
          <Reveal delay={0.15}>
            <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
              {[
                "Up to 5 custom pages",
                "Lead form + Calendly embed",
                "On-page SEO + SSL",
                "1 year of hosting included",
                "One feedback round",
                "30 days of free tweaks",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-slate-200">
                  <Check size={16} className="mt-0.5 shrink-0 text-volt" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            className="card-surface grain relative overflow-hidden border-volt/25 p-6 sm:p-8"
            data-testid="pricing-card-800"
          >
            <div
              className="absolute inset-0 opacity-50"
              style={{ background: "radial-gradient(circle at 80% 0%, rgba(0,240,255,0.12), transparent 55%)" }}
              aria-hidden="true"
            />
            <div className="relative">
              <SpotsBadge testId="pricing-spots-left-count" />
              <div className="mt-5 flex flex-wrap items-end gap-x-3 gap-y-1">
                <span className="font-display text-6xl font-extrabold leading-none tracking-tight text-white sm:text-7xl">
                  ${SITE.price}
                </span>
                <span className="pb-1 font-mono text-xs uppercase tracking-[0.15em] text-dim">
                  one-time
                </span>
              </div>
              <p className="mt-3 font-display text-lg font-bold text-volt glow-text">
                Live in 72 hours · hosting included
              </p>
              <p className="mt-3 text-sm leading-relaxed text-mist">
                Founding pricing for the first {SITE.totalSpots} customers.
                {SITE.showPaymentSplit && " Split it: 50% to start, 50% before launch."}
              </p>
              <DemoFirstNote className="mt-4 w-full" testId="pricing-demo-first-note" />
              <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-dim">
                <FileWarning size={14} className="mt-0.5 shrink-0" />
                No refunds — that's why the demo is free and you see your first cut in 48 hours.
              </p>
              <Link
                to="/book-demo"
                data-testid="pricing-card-cta"
                className="btn-primary mt-6 w-full sm:w-auto"
              >
                Book a Free Demo <ArrowRight size={16} strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* WHY TRUST US */}
    <section className="border-y border-white/5 bg-card/40 py-14 sm:py-20" data-testid="trust-section">
      <div className="container-x">
        <SectionHead
          eyebrow="Why trust us"
          title="We're new. So we earn trust with clarity."
        />
        <div className="mt-8 grid gap-3 sm:mt-10 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
          {TRUST.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.07}>
              <div className="card-surface h-full p-5">
                <item.icon size={18} className="text-volt" />
                <h3 className="mt-3 font-display text-base font-semibold text-white">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-mist">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="container-x py-14 sm:py-20" data-testid="faq-section">
      <div className="grid gap-8 lg:grid-cols-[1fr_1.4fr] lg:gap-12">
        <div>
          <SectionHead eyebrow="FAQ" title="Straight answers, including the uncomfortable ones" />
          <Reveal delay={0.2}>
            <Link to="/book-demo" data-testid="faq-cta" className="btn-primary mt-6">
              Book a Free Demo <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.08}>
          <FAQAccordion />
        </Reveal>
      </div>
    </section>

    <CTABand />
  </>
);

export default Home;
